/* @section: about-page */
import { motion } from "framer-motion";
import PageLayout from "@/components/Layout";
import { LOGO_URL, orgInfo, missionVision, coreValues, geographicAreas, partners } from "@/data";
import { MapPin, Calendar, User } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] as [number,number,number,number] } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.1 } } };

function PageHero() {
  return (
    <section className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(135deg, oklch(0.88 0.12 225), oklch(0.76 0.16 235))" }}>
      <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 70% 50%, oklch(0.99 0.01 50) 0%, transparent 60%)" }} />
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold uppercase tracking-widest bg-white/50 px-3 py-1 rounded-full border border-primary/30" style={{ color: "oklch(0.35 0.15 228)" }}>About Us</span>
            <h1 className="text-4xl md:text-5xl font-bold mt-3 mb-4 leading-tight" style={{ color: "oklch(0.15 0.08 235)" }}>About Future Light<br />Youth Network</h1>
          </motion.div>
          <motion.p variants={fadeUp} className="text-lg leading-relaxed" style={{ color: "oklch(0.25 0.06 230)" }}>
            A youth-led civil society organization empowering communities across Southern Shan State, Myanmar since 2018.
          </motion.p>
          <motion.p variants={fadeUp} className="mt-2" style={{ color: "oklch(0.35 0.06 228)", fontFamily: "'Noto Sans Myanmar', sans-serif", lineHeight: 1.9 }}>
            ၂၀၁၈ ခုနှစ်မှစ၍ တောင်ရှမ်းပြည်နယ်ရှိ လူမှုအသိုက်အဝန်းများကို ခွန်အားပေးနေသော လူငယ်ဦးဆောင်သောအဖွဲ့အစည်း
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}

function OrgOverview() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.div variants={fadeUp}>
              <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Organization Overview</span>
              <h2 className="text-3xl font-bold mt-2 mb-5">Who We Are</h2>
            </motion.div>
            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed mb-4">
              <strong className="text-foreground">Future Light Youth Network (FLYN)</strong> was established on March 15, 2018 in Pindaya Township, Danu Self-Administered Zone, Southern Shan State, under the leadership of U Zin Ko Win.
            </motion.p>
            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed mb-4">
              We are a registered, youth-led, non-governmental civil society organization committed to promoting local community development, sustainable livelihoods, and humanitarian assistance across three townships in Southern Shan State.
            </motion.p>
            <motion.p variants={fadeUp} className="text-muted-foreground leading-relaxed mb-6" style={{ fontFamily: "'Noto Sans Myanmar', sans-serif", lineHeight: 1.9 }}>
              ကျွန်ပ်တို့ FLYN သည် ဒေသဖွံ့ဖြိုးရေးလုပ်ငန်းများနှင့် လူသားချင်းစာနာထောက်ထားမှုအကူအညီပေးရေးလုပ်ငန်းများကို အားစိုက်လုပ်ဆောင်နေပြီး ပညာရှင်ဆန်သောနှင့် ဘေးကင်းလှုံခြုံသောအဖွဲ့အစည်းဖြစ်ရေးကို ဦးတည်လုပ်ဆောင်လျက်ရှိသည်။
            </motion.p>
            <motion.div variants={fadeUp} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: <Calendar className="w-5 h-5" />, label: "Founded", value: orgInfo.founded },
                { icon: <User className="w-5 h-5" />, label: "Director", value: orgInfo.director },
                { icon: <MapPin className="w-5 h-5" />, label: "Headquarters", value: orgInfo.hqCity + " Township" },
                { icon: <MapPin className="w-5 h-5" />, label: "Branch Office", value: orgInfo.branchCity + " Township" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 p-4 rounded-xl bg-muted/50 border border-border">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">{item.icon}</div>
                  <div>
                    <p className="text-xs text-muted-foreground">{item.label}</p>
                    <p className="font-semibold text-sm">{item.value}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="rounded-2xl overflow-hidden border border-border" style={{ background: "linear-gradient(135deg, oklch(0.22 0.06 170), oklch(0.15 0.04 200))" }}>
              <div className="p-8 flex flex-col items-center text-center">
                <img src={LOGO_URL} alt="FLYN Logo" className="w-24 h-24 rounded-full object-contain bg-white/15 p-2 mb-5" />
                <h3 className="text-xl font-bold text-white mb-2">{orgInfo.name}</h3>
                <p className="text-accent font-mono text-sm mb-4">{orgInfo.shortName}</p>
                <p className="text-white/60 text-sm leading-relaxed">{orgInfo.type}</p>
              </div>
              <div className="border-t border-white/10 p-6">
                <p className="text-xs text-white/40 font-mono mb-3 uppercase tracking-wider">Contact</p>
                <p className="text-white/70 text-sm mb-2">{orgInfo.address}</p>
                <a href={`mailto:${orgInfo.email}`} className="text-accent text-sm hover:underline break-all">{orgInfo.email}</a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Our Purpose</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Mission & Vision</h2>
          </motion.div>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            { label: "Mission", icon: "🎯", color: "primary", text: missionVision.mission },
            { label: "Vision", icon: "🔭", color: "accent", text: missionVision.vision },
          ].map((item) => (
            <motion.div key={item.label} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="p-8 rounded-2xl border border-border bg-background hover:shadow-lg transition-all duration-300">
              <div className="text-4xl mb-4">{item.icon}</div>
              <span className={`text-xs font-mono font-semibold uppercase tracking-widest ${item.color === "primary" ? "text-primary" : "text-accent"}`}>{item.label}</span>
              <p className="text-foreground text-lg font-medium leading-relaxed mt-3">"{item.text}"</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CoreValuesSection() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">What We Stand For</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Our Core Values</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {coreValues.map((val) => (
            <motion.div key={val.title} variants={fadeUp} className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <div className="text-3xl mb-4">{val.icon}</div>
              <h3 className="font-bold text-base mb-2">{val.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{val.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function HistoryTimeline() {
  const milestones = [
    { year: "2018", title: "Organization Founded", desc: "Established on March 15, 2018 in Pindaya Township. Formed community groups in 12 villages, established Board of Directors and financial management systems." },
    { year: "2020–21", title: "COVID-19 Response", desc: "Awareness campaigns, PPE distribution, handwashing stations, agricultural support, and WASH training across Southern Shan State." },
    { year: "2022–24", title: "VSLA & LGBTQIA+", desc: "Established 18 Village Savings & Loan Associations (458 members). Launched Colorful Hands Working Together with UNFPA for LGBTQIA+ support." },
    { year: "2023–25", title: "SCLR Programme", desc: "Implemented 32 community micro-projects including roads, water systems, health clinics, and ECCD centers under the SCLR/KKN programme." },
    { year: "2025", title: "Earthquake Emergency", desc: "Rapid response to Inle Lake earthquake — reached 1,951 households (8,436 individuals) across 16 villages with food and NFI distributions." },
    { year: "2026+", title: "Scaling Impact", desc: "Expanding HELP and CARE projects, targeting 5,800+ beneficiaries with focus on livelihoods, climate resilience, and conflict mitigation." },
  ];
  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Our Journey</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Organization History</h2>
          </motion.div>
        </motion.div>
        <div className="max-w-3xl mx-auto relative">
          <div className="absolute left-8 top-0 bottom-0 w-px bg-border" />
          <div className="space-y-8">
            {milestones.map((m, i) => (
              <motion.div key={m.year} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className="flex gap-6">
                <div className="relative flex-shrink-0">
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center text-xs font-bold font-mono text-primary-foreground text-center leading-tight" style={{ background: "linear-gradient(135deg, oklch(0.48 0.12 170), oklch(0.38 0.10 170))" }}>{m.year}</div>
                </div>
                <div className="pt-2 pb-4">
                  <h3 className="font-bold text-base mb-2">{m.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PartnersGrid() {
  return (
    <section className="py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
          <motion.div variants={fadeUp}>
            <span className="text-xs font-mono font-semibold text-primary uppercase tracking-widest">Collaboration</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-2">Partners & Donors</h2>
          </motion.div>
        </motion.div>
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {partners.map((p) => (
            <motion.div key={p.name} variants={fadeUp} className="p-6 rounded-2xl bg-card border border-border hover:border-primary/40 hover:shadow-md transition-all duration-300">
              <h3 className="font-bold text-base mb-1">{p.name}</h3>
              <p className="text-xs text-primary mb-1 font-medium">{p.type}</p>
              <p className="text-xs text-muted-foreground">{p.country}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

export default function About() {
  return (
    <PageLayout>
      <PageHero />
      <OrgOverview />
      <MissionVision />
      <CoreValuesSection />
      <HistoryTimeline />
      <PartnersGrid />
    </PageLayout>
  );
}
